import _ from 'lodash';

import { TaskStatus } from 'constants/tasks';

import { toPatchFormData, toTask } from './taskApi';
import { MULTIPART_CONFIG, TASK_INCLUDE_CONFIG, TASKS_URL } from './taskApi.constants';

const normalizeTask = task => {
  if (!task) {
    return task;
  }

  const mappedTask = toTask(task);

  return {
    ...mappedTask,
    name: mappedTask.name ?? '',
    availableForAssignees: mappedTask.availableForAssignees ?? false,
    targetType: mappedTask.targetType ?? null,
    assignedUserId: mappedTask.assignedUserId ?? null,
  };
};

const normalizeTaskList = data => (Array.isArray(data) ? data.map(normalizeTask) : []);

const buildCreateBody = ({
  subCompanyId,
  assignedUserId,
  taskTypeId,
  name,
  description,
  availableForAssignees,
}) => ({
  subCompanyId,
  assignedUserId,
  requests: [
    {
      taskTypeId,
      name,
      description: description || name,
      availableForAssignees,
      metadata: null,
    },
  ],
});

export class SubCompanyTaskApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async #updateTask({ taskId, patch, files }) {
    const response = await this.#http.put(
      `${TASKS_URL}/${taskId}`,
      toPatchFormData(patch, files),
      MULTIPART_CONFIG,
    );

    return { ...response, data: normalizeTask(response.data) };
  }

  async getSubCompanyTasks({ subCompanyId, assignedUserId, signal }) {
    const response = await this.#http.get(TASKS_URL, {
      ...TASK_INCLUDE_CONFIG,
      params: {
        ...TASK_INCLUDE_CONFIG.params,
        subCompanyId,
        subCompanyTasksOnly: true,
        ...(assignedUserId && { assignedUserId }),
      },
      signal,
    });

    return { ...response, data: normalizeTaskList(response.data) };
  }

  async createSubCompanyTask(fields) {
    const response = await this.#http.post(`${TASKS_URL}/subcompany`, buildCreateBody(fields));

    const created = Array.isArray(response.data) ? response.data[0] : response.data;

    return { ...response, data: normalizeTask(created) };
  }

  async submitSubCompanyTask({ taskId, file, submittedText }) {
    return this.#updateTask({
      taskId,
      patch: {
        status: TaskStatus.SUBMITTED,
        submittedText: _.trim(submittedText) ? submittedText : null,
        isDedicatedRouteUpdate: true,
      },
      files: file ? [file] : [],
    });
  }

  async approveSubCompanyTask({ taskId }) {
    return this.#updateTask({
      taskId,
      patch: { status: TaskStatus.APPROVED, isDedicatedRouteUpdate: true },
    });
  }

  async updateSubCompanyTaskStatus({ taskId, status, userNote, assignedUserId }) {
    const patch = {
      status,
      ...(userNote && { userNote }),
      ...(assignedUserId && { assigneeUpdate: { assignedUserId } }),
    };

    return this.#http.put(`${TASKS_URL}/${taskId}`, toPatchFormData(patch), MULTIPART_CONFIG);
  }

  async postSubCompanyTaskMessage({ taskId, message, audience }) {
    return this.#http.post(`${TASKS_URL}/${taskId}/messages`, { message, audience });
  }

  async getSubCompanyTaskHistory({ taskId }) {
    return this.#http.get(`${TASKS_URL}/${taskId}/history`);
  }
}
